import { useCallback, useState } from 'react';

import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useReportError } from '@/hooks/useReportError';
import { useMultiDocumentUploaderContext } from '@/modules/documents/MultiDocumentUploader/context/multiDocumentUploader.context';
import { DocumentType } from '@/types/schema';

interface TriggerAIOnboardingWorkflowParams {
  householdId: string;
  onSuccess?: () => void;
  onError?: () => void;
  onDocumentsUploaded?: (documentIds: string[]) => void;
}

interface UseTriggerAIOnboardingWorkflowReturn {
  loading: boolean;
  triggerWorkflow: (params: TriggerAIOnboardingWorkflowParams) => Promise<void>;
}

/**
 * This hook provides a function to upload documents within the
 * MultiDocumentUploaderContext. The AI onboarding workflow is automatically
 * triggered by the backend when documents are uploaded.
 *
 * This will handle any errors that occur during the upload process.
 */
export function useTriggerAIOnboardingWorkflow(): UseTriggerAIOnboardingWorkflowReturn {
  const { reportError } = useReportError();
  const { showFeedback } = useFeedback();

  const [loading, setLoading] = useState(false);

  const { uploadDocuments, uploadedFiles, selectedExistingDocuments } =
    useMultiDocumentUploaderContext();

  const triggerWorkflow = useCallback(
    async ({
      householdId,
      onSuccess,
      onError,
      onDocumentsUploaded,
    }: TriggerAIOnboardingWorkflowParams) => {
      setLoading(true);

      try {
        const allDocumentIds = [
          ...selectedExistingDocuments.map((doc) => doc.id),
        ];
        // Upload the documents...
        // This will throw on error, which we handle in the catch block.
        if (uploadedFiles.length) {
          const createdDocumentIds = await uploadDocuments({
            householdId,
            defaultDocumentType: DocumentType.Other,
          });
          allDocumentIds.push(...createdDocumentIds);
        }

        // Verify at least 1 document was actually successfully uploaded.
        // This is more of a sanity check at this point.
        if (!allDocumentIds.length) {
          setLoading(false);
          reportError(
            'useTriggerAIOnboardingWorkflow: bulk upload documents succeeded, but 0 documents were returned'
          );
          showFeedback('Error uploading documents. Please try again.');
          onError?.();
          return;
        }

        // Documents uploaded successfully. The backend will automatically
        onDocumentsUploaded?.(allDocumentIds);
        setLoading(false);
        onSuccess?.();
      } catch (e) {
        setLoading(false);
        reportError(
          'useTriggerAIOnboardingWorkflow: error bulk uploading documents',
          e as Error
        );
        showFeedback('Error uploading documents. Please try again.');
        onError?.();
      }
    },
    [
      reportError,
      selectedExistingDocuments,
      showFeedback,
      uploadDocuments,
      uploadedFiles.length,
    ]
  );

  return {
    loading,
    triggerWorkflow,
  };
}
